import { AlertTriangle, ShoppingCart, Sparkles, TrendingUp } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { cn } from "../ui/utils";

export type AtGlanceInsightsProps = {
  insights: {
    critical?: string | null;
    growth?: string | null;
    upsell?: string | null;
    revenue?: string | null;
  } | null;
  /** `strip` sits under the page intro as a headline summary; `card` is a standalone card. */
  variant?: "strip" | "card";
};

const rows: Array<{
  key: keyof NonNullable<AtGlanceInsightsProps["insights"]>;
  icon: typeof AlertTriangle;
  label: string;
}> = [
  { key: "critical", icon: AlertTriangle, label: "Stock risk" },
  { key: "growth", icon: TrendingUp, label: "Growth" },
  { key: "upsell", icon: ShoppingCart, label: "Basket" },
  { key: "revenue", icon: Sparkles, label: "Revenue" },
];

export function AtGlanceInsights({ insights, variant = "strip" }: AtGlanceInsightsProps) {
  if (!insights) return null;

  const lines = rows
    .map(({ key, icon: Icon, label }) => {
      const text = insights[key];
      if (text == null || String(text).trim() === "") return null;
      return (
        <div
          key={key}
          className="flex gap-3 sm:gap-4 items-start text-sm text-foreground min-w-0"
        >
          <Icon className="w-4 h-4 shrink-0 mt-0.5 text-muted-foreground" aria-hidden />
          <div className="flex flex-col gap-0.5 min-w-0 sm:flex-row sm:gap-2 sm:items-baseline">
            <span className="text-xs font-medium text-muted-foreground shrink-0 sm:w-28">{label}</span>
            <span className="text-sm leading-snug">{text}</span>
          </div>
        </div>
      );
    })
    .filter(Boolean);

  if (lines.length === 0) return null;

  if (variant === "card") {
    return (
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">At a glance</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 pt-0">{lines}</CardContent>
      </Card>
    );
  }

  return (
    <div
      className={cn(
        "rounded-xl border border-border/80 bg-muted/30 px-4 py-4 sm:px-5",
        "shadow-sm",
      )}
      aria-labelledby="analytics-at-a-glance-heading"
    >
      <h2 id="analytics-at-a-glance-heading" className="text-sm font-semibold text-foreground mb-3">
        At a glance
      </h2>
      <div className="grid gap-3 sm:grid-cols-2 sm:gap-x-8 sm:gap-y-3">{lines}</div>
    </div>
  );
}
